import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { applyRequiredCapitalizations } from '../../../util/TextUtils';
import TextField from '../TextField';
import TimeZonePicker from '../TimeZonePicker';
import _ from 'underscore';
import moment from 'moment-timezone';
import { Grid, Row, Col } from 'react-bootstrap';
import AppointmentTimePicker from '../TimePicker/AppointmentTimePicker';
import { theme } from '../../../../../themes';
import partnerStyles from '../../../config/partnerStylesConfig';

class ApptScheduler extends Component {
  constructor(props) {
    super(props);

    const partnerStyle = partnerStyles.appScheduler;

    this.state = {
      textFields: [
        {
          position: 1,
          id: 'customerFirstName',
          label: 'FULL NAME',
          placeholder: 'John Doe',
          validationRegex: /^[a-zA-Z\s]{1,50}$/,
          isValid: true,
          helperText: '',
          error: 'Please enter up to 50 alphabetic characters',
          autoFocus: true,
          xsCols: 12,
          type: 'text',
          supportedModes: ['callMeNow', 'callMeLater'],
        },
        {
          position: 2,
          id: 'customerCallbackNumber',
          label: 'PHONE NUMBER',
          placeholder: "We'll call this number",
          validationRegex: /^\(?[0-9]{3}\)?[ ]?[0-9]{3}-?[0-9]{4}$/,
          isValid: true,
          helperText: '',
          error: 'Please enter a valid 10 digit phone number',
          autoFocus: false,
          xsCols: 12,
          type: 'text',
          supportedModes: ['callMeNow', 'callMeLater'],
        },
        {
          position: 3,
          id: 'customerEmailAddress',
          label: 'EMAIL ADDRESS',
          placeholder: 'johndoe@email.com',
          validationRegex: /^(\S+)\@(\S+)\.(\S+)$/,
          isValid: true,
          helperText: '',
          error: 'Please enter a valid email address',
          autoFocus: false,
          xsCols: 12,
          type: 'text',
          supportedModes: ['callMeLater'],
        },
      ],
      partnerStyle,
    };
  }

  onChange = (e) => {
    if (!e || !e.target) {
      return;
    }

    if (_.isFunction(e.preventDefault)) {
      e.preventDefault();
    }

    const { appointmentData } = this.props;
    // Note: following lines order is important!
    const isDeleting =
      appointmentData[e.target.id] && e.target.value && appointmentData[e.target.id].length > e.target.value.length;
    appointmentData[e.target.id] = e.target.value;

    const validationField = this.state.textFields.find((field) => field.id === e.target.id && field.validationRegex);
    if (validationField) {
      validationField.isValid = !!e.target.value.match(validationField.validationRegex);
      if (e.target.id === 'customerCallbackNumber' && !isDeleting) {
        const formattedNumber = this.getFormattedUSPhoneNumber(e.target.value);
        appointmentData[e.target.id] = formattedNumber;
        validationField.isValid = !!formattedNumber.match(validationField.validationRegex);
      }
    }
    this.props.onApptSchedulerChange(appointmentData);
  };

  // A valid phone adheres to the validation regex above for customerCallbackNumber:
  // validationRegex : /^\(?[0-9]{3}\)?[ ]?[0-9]{3}-?[0-9]{4}$/
  getFormattedUSPhoneNumber = (phoneNumber) => {
    if (!_.isString(phoneNumber)) {
      return '';
    }

    // Need to allow the user to enter a leading openning parenthesis.
    if (phoneNumber.length === 1 && phoneNumber.charAt(0) === '(') {
      return phoneNumber;
    } else if (phoneNumber.length === 2 && phoneNumber.charAt(0) === '(' && isNaN(phoneNumber.charAt(1))) {
      return '(';
    }

    // Remove all non-digit characters.
    let formattedPhoneNumber = phoneNumber.replace(/\D/g, '');
    // Start formatting process:
    if (formattedPhoneNumber.length < 3 && !isNaN(parseInt(formattedPhoneNumber))) {
      formattedPhoneNumber = '(' + formattedPhoneNumber.substring(0);
    } else if (formattedPhoneNumber.length === 3) {
      formattedPhoneNumber = '(' + formattedPhoneNumber.substring(0, 3) + ') ';
    } else if (formattedPhoneNumber.length > 3 && formattedPhoneNumber.length < 6) {
      formattedPhoneNumber = '(' + formattedPhoneNumber.substring(0, 3) + ') ' + formattedPhoneNumber.substring(3);
    } else if (formattedPhoneNumber.length === 6) {
      formattedPhoneNumber =
        '(' + formattedPhoneNumber.substring(0, 3) + ') ' + formattedPhoneNumber.substring(3) + '-';
    } else if (formattedPhoneNumber.length > 6) {
      formattedPhoneNumber =
        '(' +
        formattedPhoneNumber.substring(0, 3) +
        ') ' +
        formattedPhoneNumber.substring(3, 6) +
        '-' +
        formattedPhoneNumber.substring(6);
    }
    return formattedPhoneNumber;
  };

  getFormattedTime = (time, timezone) => {
    if (!time || !timezone) {
      return '';
    }
    const momentTime = moment.tz(time, timezone);
    return momentTime.format('MMMM Do') + ' at ' + momentTime.format('h:mm A z');
  };

  getApptTimeString = () => {
    let customerAppointmentDateTimeString = '';
    if (this.props.appointmentData.customerAppointmentDateTime) {
      customerAppointmentDateTimeString = this.getFormattedTime(
        this.props.appointmentData.customerAppointmentDateTime,
        this.props.appointmentData.timezone
      );
    }
    return customerAppointmentDateTimeString;
  };

  renderUserInfoInputs = () => {
    const formControlClasses = `form-container`;
    return (
      <Grid>
        <Row className="user-info">
          {this.state.textFields
            .filter((t) => t.supportedModes.find((mode) => mode === this.props.mode))
            .sort((t1, t2) => t1.position - t2.position)
            .map((textField) => {
              const textFieldValue = this.props.appointmentData[textField.id];
              return (
                <Col xs={textField.xsCols} sm={textField.xsCols} md={6} lg={6} key={textField.position}>
                  <TextField
                    label={applyRequiredCapitalizations(textField.label)}
                    id={textField.id}
                    classes={{ formControl: formControlClasses }}
                    onChange={this.onChange}
                    placeholder={textField.placeholder}
                    value={textFieldValue ? textFieldValue : ''}
                    helperText={textField.helperText}
                    error={textField.isValid ? '' : <span className="errorField">{textField.error}</span>}
                    autoFocus={textField.autoFocus}
                    tabindex={textField.id}
                    inputProps={{ disabled: this.props.isDisabled, style: { ...this.state.partnerStyle.inputStyle } }}
                    type={textField.type}
                    style={{ color: theme.colors.primaryAccentOnWhite }}
                  />
                </Col>
              );
            })}
          {this.props.mode !== 'callMeNow' && (
            <Col xs={12} sm={12} md={6} lg={6}>
              <TimeZonePicker
                classes={{ formControl: formControlClasses }}
                id="timezone"
                defaultValue={this.props.appointmentData.timezone}
                onChange={this.onChange}
                isDisabled={this.props.isDisabled}
                style={{ color: theme.colors.primaryAccentOnWhite }}
                selectProps={{ style: { ...this.state.partnerStyle.timeZonePickerStyle } }}
              />
            </Col>
          )}
        </Row>
      </Grid>
    );
  };

  renderAppointmentTimePicker = () => {
    if (this.props.mode === 'callMeNow') {
      return null;
    }

    if (!this.props.appointmentData.timezone || this.props.appointmentData.timezone === 'choose') {
      return (
        <div id="common-appointment-time-picker-container">
          <div id="common-appointment-time-picker">Please select time zone</div>
        </div>
      );
    }

    const requestedTimeString = this.getApptTimeString();
    return (
      <div id="common-appointment-time-picker-container">
        <AppointmentTimePicker
          id="customerAppointmentDateTime"
          zoneId={this.props.appointmentData.timezone}
          onChange={this.onChange}
          vhtSegment={this.props.appointmentData.vhtSegment}
        />
        {!!requestedTimeString && (
          <div id="requestedTime">
            <span className="title">{applyRequiredCapitalizations('REQUESTED TIME:')}</span>
            <span className="time" style={{ color: theme.colors.linkColorDark }}>
              {requestedTimeString}
            </span>
          </div>
        )}
      </div>
    );
  };

  render() {
    return (
      <div className="apptSchedulerContainer">
        {this.renderUserInfoInputs()}
        {this.renderAppointmentTimePicker()}
      </div>
    );
  }
}

ApptScheduler.propTypes = {
  onApptSchedulerChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state /*, ownProps*/) => ({
  mode: state.experiences.experienceBookingModal,
  isDisabled: state.experiences.experienceBookingInProgress,

  appointmentData: {
    appointmentType: state.experiences.appointmentData.appointmentType,
    customerFirstName: state.experiences.appointmentData.customerFirstName,
    // customerLastName: state.experiences.appointmentData.customerLastName,
    customerCallbackNumber: state.experiences.appointmentData.customerCallbackNumber,
    customerEmailAddress: state.experiences.appointmentData.customerEmailAddress,
    timezone: state.experiences.appointmentData.timezone,
    customerAppointmentDateTime: state.experiences.appointmentData.customerAppointmentDateTime,
    customerAppointmentDateTimeString: state.experiences.appointmentData.customerAppointmentDateTimeString,
    vhtSegment: state.experiences.experienceSelected.vhtSegment,
    megaUseCase: state.experiences.experienceSelected.megaUseCase,
    customerSessionId: state.experiences.customerSessionId,
    distinctId: state.experiences.distinctId,
    repBrowserInfo: state.experiences.repBrowserInfo,
    repDeviceInfo: state.experiences.repDeviceInfo,
    sourceDomain: state.experiences.sourceDomain,
  },
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApptScheduler);



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/experiences/components/common/ApptScheduler/ApptScheduler.js